import { FC, useCallback, useEffect, useRef, useState, ComponentProps } from 'react';
import {
  Box,
  ClickAwayListener,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';
import type { GlobalSearchResult } from '@/services/api/search';
import Icon from '@/components/ui/Icon';
// Direct-Import (DESIGN_SYSTEM Teil B).
import SearchField from '@/components/ui/shared/SearchField';

type IconName = ComponentProps<typeof Icon>['icon'];

const TYPE_ICON: Record<GlobalSearchResult['type'], IconName> = {
  case: 'Document',
  meeting: 'Calendar',
  task: 'Checked',
  card: 'Section',
};

const GlobalSearchDropdown: FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doSearch = useCallback(async (term: string) => {
    if (term.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const data = await api.search.global(term, controller.signal);
      if (!controller.signal.aborted) {
        setResults(data);
        setOpen(true);
        setActiveIndex(-1);
      }
    } catch {
      // Fehler wird im API-Layer behandelt
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  const handleChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 2) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => doSearch(value), 300);
  };

  const handleSelect = (result: GlobalSearchResult) => {
    setOpen(false);
    setQuery('');
    if (result.target === '_blank') {
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(result.url);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0 && results[activeIndex]) {
      e.preventDefault();
      handleSelect(results[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ position: 'relative' }}>
        <SearchField
          inputRef={inputRef}
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Suchen..."
          width={280}
          hoehe={32}
          loading={loading}
          aria-label="Globale Suche"
        />
        <Popper
          open={open && query.length >= 2}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          sx={{ zIndex: 1300, width: 380 }}
        >
          <Paper
            sx={{
              // Design-Rollout: Popover-Optik aus dem Theme (8px, Hairline,
              // leichter Schatten nur ueber MuiPopover/MuiPaper).
              mt: 1,
              borderRadius: '8px',
              border: 1,
              borderColor: 'divider',
              maxHeight: 420,
              overflow: 'auto',
            }}
          >
            {results.length === 0 && !loading ? (
              <Typography variant="meta" sx={{ px: 4, py: 4, textAlign: 'center' }}>
                Kein Ergebnis gefunden
              </Typography>
            ) : (
              results.map((result, index) => (
                // FB 214.1: echter Anker statt reinem onClick-Handler. Damit
                // uebernimmt der BROWSER Mittelklick, Strg-/Cmd-Klick und
                // "Link in neuem Tab oeffnen" aus seinem eigenen Kontextmenue —
                // nachbauen liesse sich das nur unvollstaendig. Der linke
                // Klick ohne Modifier wird weiterhin abgefangen und laeuft als
                // SPA-Navigation, sonst gaebe es bei jedem Treffer einen
                // vollen Seiten-Reload.
                <Stack
                  key={`${result.type}-${result.id}`}
                  component="a"
                  href={result.url}
                  target={result.target === '_blank' ? '_blank' : undefined}
                  rel={result.target === '_blank' ? 'noopener noreferrer' : undefined}
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  onClick={(e: React.MouseEvent) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                    e.preventDefault();
                    handleSelect(result);
                  }}
                  sx={{
                    px: 2,
                    py: 1.25,
                    cursor: 'pointer',
                    textDecoration: 'none',
                    color: 'inherit',
                    bgcolor: index === activeIndex ? 'primary.hover' : 'transparent',
                    '&:hover': { bgcolor: 'primary.hover' },
                    borderBottom: index < results.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      bgcolor: 'primary.hover',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon icon={TYPE_ICON[result.type]} size={18} sx={{ color: 'primary.main' }} />
                  </Box>
                  <Stack flex={1} sx={{ minWidth: 0 }}>
                    <Typography noWrap variant="wertBetont">
                      {result.title}
                    </Typography>
                    {result.subtitle && (
                      <Typography noWrap variant="klein">
                        {result.subtitle}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              ))
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearchDropdown;
